'use client';
import React, { useState, useEffect } from 'react';
import PublicTopbar from '@/components/PublicTopbar';
import PublicFooter from '@/app/components/PublicFooter';
import AppImage from '@/components/ui/AppImage';
import { X, ChevronLeft, ChevronRight, ZoomIn, Loader2, Image as ImageIcon } from 'lucide-react';

interface GalleryImage {
  id: string;
  src: string;
  alt: string;
  caption: string;
  category: string;
  span?: 'wide' | 'tall' | 'normal';
}

const categories = ['All', 'Live', 'Studio', 'Press', 'Events', 'Behind the Scenes'];

export default function GalleryPage() {
  const [activeCategory, setActiveCategory] = useState('All');
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [images, setImages] = useState<GalleryImage[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch('/api/gallery')
      .then((r) => r.json())
      .then((data) => setImages(data.images ?? []))
      .catch(() => setImages([]))
      .finally(() => setLoading(false));
  }, []);

  const filtered = images.filter((img) => activeCategory === 'All' || img.category === activeCategory);

  const openLightbox = (index: number) => setLightboxIndex(index);
  const closeLightbox = () => setLightboxIndex(null);
  const prevImage = () => setLightboxIndex((i) => i !== null ? (i - 1 + filtered.length) % filtered.length : null);
  const nextImage = () => setLightboxIndex((i) => i !== null ? (i + 1) % filtered.length : null);

  return (
    <div className="min-h-screen bg-background">
      <PublicTopbar />

      {/* Header */}
      <section className="pt-32 pb-12 bg-background">
        <div className="max-w-screen-2xl mx-auto px-6 lg:px-10">
          <p className="text-[11px] font-600 tracking-widest uppercase text-primary mb-2">Photography</p>
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6">
            <h1 className="text-5xl font-800 text-foreground">Gallery</h1>
            <div className="flex items-center gap-2 flex-wrap">
              {categories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setActiveCategory(cat)}
                  className={`px-3 py-1.5 text-xs font-600 rounded-lg transition-all ${
                    activeCategory === cat
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted/40 text-muted-foreground hover:text-foreground hover:bg-muted/70'
                  }`}>
                  {cat}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Masonry grid */}
      <section className="pb-24 bg-background">
        <div className="max-w-screen-2xl mx-auto px-6 lg:px-10">
          {loading ? (
            <div className="flex justify-center py-24">
              <Loader2 size={32} className="animate-spin text-primary" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-24">
              <ImageIcon size={48} className="text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No images in this category yet.</p>
            </div>
          ) : (
            <div className="columns-1 sm:columns-2 lg:columns-3 xl:columns-4 gap-4 space-y-4">
              {filtered.map((image, index) => (
                <div
                  key={image.id}
                  className="group relative break-inside-avoid rounded-xl overflow-hidden bg-card border border-border card-glow-hover cursor-pointer transition-all duration-200"
                  onClick={() => openLightbox(index)}>
                  <div className={`relative overflow-hidden ${image.span === 'wide' ? 'aspect-[16/9]' : image.span === 'tall' ? 'aspect-[3/4]' : 'aspect-square'}`}>
                    <AppImage
                      src={image.src}
                      alt={image.alt}
                      fill
                      className="object-cover group-hover:scale-110 transition-transform duration-500"
                      sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 25vw"
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors duration-200" />
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                      <div className="p-3 rounded-full bg-white/20 backdrop-blur-sm">
                        <ZoomIn size={20} className="text-white" />
                      </div>
                    </div>
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                    <p className="text-xs font-500 text-white/90">{image.caption}</p>
                    <span className="text-[10px] text-white/50">{image.category}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Lightbox */}
      {lightboxIndex !== null && (
        <div
          className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center animate-fade-in"
          onClick={closeLightbox}>
          <button
            onClick={closeLightbox}
            className="absolute top-4 right-4 z-10 p-2 rounded-full bg-white/10 text-white hover:bg-white/20 transition-all"
            aria-label="Close lightbox">
            <X size={24} />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); prevImage(); }}
            className="absolute left-4 top-1/2 -translate-y-1/2 z-10 p-3 rounded-full bg-white/10 text-white hover:bg-white/20 transition-all"
            aria-label="Previous image">
            <ChevronLeft size={24} />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); nextImage(); }}
            className="absolute right-4 top-1/2 -translate-y-1/2 z-10 p-3 rounded-full bg-white/10 text-white hover:bg-white/20 transition-all"
            aria-label="Next image">
            <ChevronRight size={24} />
          </button>

          <div
            className="relative max-w-5xl max-h-[85vh] w-full mx-16 animate-scale-in"
            onClick={(e) => e.stopPropagation()}>
            <div className="relative w-full h-full max-h-[75vh]">
              <AppImage
                src={filtered[lightboxIndex].src}
                alt={filtered[lightboxIndex].alt}
                fill
                className="object-contain"
                sizes="100vw"
              />
            </div>
            <div className="mt-4 text-center">
              <p className="text-sm font-500 text-white/90">{filtered[lightboxIndex].caption}</p>
              <p className="text-xs text-white/40 mt-1">{lightboxIndex + 1} / {filtered.length}</p>
            </div>
          </div>
        </div>
      )}

      <PublicFooter />
    </div>
  );
}
